import { Suspense, type ReactNode } from "react";
import { Outlet, Route, Routes } from "react-router";
import { APP_ROUTES } from "@/app/routes/config/app.routes";
import { APP_ROUTES_TRANSLATIONS } from "@/app/constants";
import { AccessGuard } from "@/components/AccessGuard";
import type { RouteConfig } from "@/app/routes/types/routeAccess";
import { getTranslation, useLocale } from "@mivo/i18n";

const RouteFallback = () => {
    useLocale();
    return (
        <div className="min-h-[40vh] flex items-center justify-center p-10 text-muted-foreground/80 text-sm">
            {getTranslation(APP_ROUTES_TRANSLATIONS.loading)}
        </div>
    );
};

/**
 * Must return `<Route>` elements (not a custom component) — React Router only
 * accepts `Route` / `Fragment` as direct children of `<Routes>`.
 */
function renderRouteConfig(config: RouteConfig, key: string): ReactNode {
    const { path, index, element, access, children } = config;
    const hasChildren = Boolean(children && children.length > 0);
    const elementToRender = element ?? (hasChildren ? <Outlet /> : null);

    const elementWithGuard =
        access && access.length > 0 ? (
            <AccessGuard requirements={access}>{elementToRender}</AccessGuard>
        ) : (
            elementToRender
        );

    const childRoutes = children?.map((child, idx) =>
        renderRouteConfig(child, `${key}-${child.path ?? "index"}-${idx}`),
    );

    if (index) {
        return <Route key={key} index element={elementWithGuard} />;
    }

    return (
        <Route key={key} path={path} element={elementWithGuard}>
            {childRoutes}
        </Route>
    );
}

/** Central declarative route tree (`APP_ROUTES`). */
export function AppRoutes() {
    return (
        <Suspense fallback={<RouteFallback />}>
            <Routes>
                {APP_ROUTES.map((route, idx) =>
                    renderRouteConfig(route, `${route.path ?? "layout"}-${idx}`),
                )}
            </Routes>
        </Suspense>
    );
}
